import React, { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { useTheme } from "../../../ThemeContext";
import { getFacilities, createFacility, editFacility } from "../onboarding-services";
import FacilityService from "../../../services/facility-service";
import { Dropdown } from "@voicecare.ai/ui-library";
import { networkPlanTypes, placeOfServiceOptions } from "../../../utils/constants/insuranceOptions";
import { 
  cleanPhoneNumber, 
  isValidUSPhoneNumber, 
  cleanTaxId, 
  isValidTaxId, 
  cleanNPI, 
  isValidNPI,
  formatPhoneNumberForEdit
} from "../../../utils/helpers/format-phone-number";
import InNetworkInsuranceField from "../../facility/InNetworkInsuranceField";
import "./StepStyles.css";

/**
 * Maps API facility object to form field names (consistent with facility-form.js)
 * 
 * @param {Object} facility - Facility object from API
 * @returns {Object} Mapped facility data for form
 */
function mapFacilityToFormValues(facility) {
  console.log('🏥 Processing facility data in mapFacilityToFormValues:', facility);
  
  // Map network plan types from API format to form format
  const mappedPlanTypeValues = Array.isArray(facility?.inNetworkInsurancePlanType)
    ? facility?.inNetworkInsurancePlanType.map(type => ({
        label: type,
        value: type
      }))
    : [];
  
  // Map place of service from API format to form format (single value)
  const mappedPlaceOfServiceValue = facility?.placeOfService || "";
  
  // Map departments from API format to form format
  console.log('🏥 Processing departments in mapFacilityToFormValues:', facility?.departments);
  const mappedDepartments = Array.isArray(facility?.departments) && facility?.departments.length > 0
    ? facility?.departments.map(dept => ({
        id: dept._id || dept.id || undefined, // Preserve ID for existing departments
        name: dept.name || "",
        faxNumber: dept.faxNumber ? formatPhoneNumberForEdit(dept.faxNumber) : ""
      }))
    : [{ name: "", faxNumber: "" }]; // Default single empty department if none exist
  
  console.log('🔧 Mapped departments for form:', mappedDepartments);
    
  const formValues = {
    facilityName: facility?.facilityName || "",
    phoneNumber: formatPhoneNumberForEdit(facility?.phoneNumber) || "",
    extension: facility?.extension || "",
    faxNumber: formatPhoneNumberForEdit(facility?.faxNumber) || "",
    groupNPI: facility?.groupNPI || "",
    taxId: facility?.groupTaxId || "",
    inNetworkIns: facility?.inNetworkInsurancePlans || [],
    networkPlanType: mappedPlanTypeValues,
    placeOfService: mappedPlaceOfServiceValue,
    streetAddress: facility?.location?.streetAddress || "",
    city: facility?.location?.city || "",
    state: facility?.location?.state || "",
    zipCode: facility?.location?.zipcode || "",
    departments: mappedDepartments,
  };
  
  console.log('📋 Final form values being returned:', formValues);
  return formValues;
}

const FacilityDetailsStep = ({ onNext, onPrevious, isLoading, setIsLoading }) => {
  const { themeMode } = useTheme();
  
  const [formData, setFormData] = useState({
    facilityName: "",
    phoneNumber: "",
    extension: "",
    faxNumber: "",
    groupNPI: "",
    taxId: "",
    inNetworkIns: [],
    networkPlanType: [],
    placeOfService: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    departments: [{ name: "", faxNumber: "" }]
  });
  
  const [errors, setErrors] = useState({});
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [facilityId, setFacilityId] = useState(null);

  const loadFacilityData = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log("🏥 Loading facility data...");
      
      // Step 1: Get facilities list to get the first facility ID
      const facilities = await getFacilities();
      console.log("🏥 Facilities list response:", facilities);
      
      if (facilities && facilities.length > 0) {
        const firstFacility = facilities[0];
        const facilityId = firstFacility._id;
        console.log("🏥 First facility ID found:", facilityId);
        setFacilityId(facilityId);
        
        try {
          // Step 2: Fetch complete facility details with departments using FacilityService.getById
          console.log("🔄 Fetching complete facility details for:", facilityId);
          const response = await FacilityService.getById(facilityId);
          
          if (response.success && response.data) {
            console.log("✅ Successfully fetched complete facility details:", response.data);
            console.log("🏥 Complete departments in response.data:", response.data.departments);
            
            // Step 3: Use the mapping function to properly format the data
            const mappedData = mapFacilityToFormValues(response.data);
            console.log("✅ Final mapped facility data:", mappedData);
            console.log("✅ Final departments array:", mappedData.departments);
            
            setFormData(mappedData);
            
            // Verify the state was set correctly
            setTimeout(() => {
              console.log("🔍 Form data after state update:", mappedData);
            }, 100);
            
          } else {
            console.error("❌ Failed to fetch complete facility details:", response.message);
            // Fallback to using the basic facility data from the list (without complete departments)
            console.log("⚠️ Falling back to basic facility data without complete departments");
            const mappedData = mapFacilityToFormValues(firstFacility);
            setFormData(mappedData);
          }
          
        } catch (detailsError) {
          console.error("❌ Error fetching complete facility details:", detailsError);
          // Fallback to using the basic facility data from the list
          console.log("⚠️ Falling back to basic facility data due to error");
          const mappedData = mapFacilityToFormValues(firstFacility);
          setFormData(mappedData);
        }
        
      } else {
        console.log("ℹ️ No facilities found, ready to create new one");
        // Ensure we have default department structure even for new facility
        setFormData(prev => ({
          ...prev,
          departments: [{ name: "", faxNumber: "" }],
          placeOfService: ""
        }));
      }
      
      setHasLoadedData(true);
    } catch (error) {
      console.error("❌ Error loading facility list:", error);
      // Ensure we have default department structure on error
      setFormData(prev => ({
        ...prev,
        departments: [{ name: "", faxNumber: "" }],
        placeOfService: ""
      }));
      setHasLoadedData(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  // Load existing facility data on component mount
  useEffect(() => {
    loadFacilityData();
  }, [loadFacilityData]);

  // Handle department changes
  const handleDepartmentChange = (index, field, value) => {
    const updatedDepartments = [...formData?.departments];
    
    // Special handling for department fax number formatting
    if (field === 'faxNumber') {
      const valueWithoutPrefix = value.replace(/^\+1\s*/, '');
      const cleaned = valueWithoutPrefix.replace(/\D/g, '');
      
      let formatted = '';
      if (cleaned.length === 0) {
        formatted = '';
      } else if (cleaned.length <= 3) {
        formatted = cleaned;
      } else if (cleaned.length <= 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
      
      updatedDepartments[index][field] = formatted;
    } else {
      updatedDepartments[index][field] = value;
    }
    
    setFormData(prev => ({ ...prev, departments: updatedDepartments }));
    
    // Clear error when user starts typing
    if (errors[`departments.${index}.${field}`]) {
      setErrors(prev => ({ ...prev, [`departments.${index}.${field}`]: null }));
    }
  };

  // Add new department
  const addDepartment = () => {
    setFormData(prev => ({
      ...prev,
      departments: [...prev.departments, { name: "", faxNumber: "" }]
    }));
  };

  // Remove department
  const removeDepartment = (index) => {
    if (formData?.departments.length > 1) {
      const updatedDepartments = formData?.departments.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, departments: updatedDepartments }));
    } else {
      // Clear the department but keep the structure
      const updatedDepartments = [...formData?.departments];
      updatedDepartments[index] = { name: "", faxNumber: "" };
      setFormData(prev => ({ ...prev, departments: updatedDepartments }));
    }
  };

  const handleInputChange = (field, value) => {
    // Special handling for phone number fields to provide formatting
    if (field === 'phoneNumber' || field === 'faxNumber') {
      // Remove the +1 prefix if user tries to modify it
      const valueWithoutPrefix = value.replace(/^\+1\s*/, '');
      
      // Remove all non-digit characters for processing
      const cleaned = valueWithoutPrefix.replace(/\D/g, '');
      
      // Format as (XXX) XXX-XXXX if we have enough digits
      let formatted = '';
      if (cleaned.length === 0) {
        formatted = '';
      } else if (cleaned.length <= 3) {
        formatted = cleaned;
      } else if (cleaned.length <= 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
      
      setFormData(prev => ({ ...prev, [field]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle key events for phone number inputs
  const handlePhoneKeyDown = (event) => {
    const input = event.target;
    const cursorPosition = input.selectionStart || 0;
    
    // Prevent deletion or modification of +1 prefix
    if ((event.key === 'Backspace' || event.key === 'Delete') && cursorPosition <= 3) {
      event.preventDefault();
      return;
    }
    
    // Prevent moving cursor into the +1 prefix with arrow keys
    if (event.key === 'ArrowLeft' && cursorPosition <= 3) {
      event.preventDefault();
      return;
    }
    
    // Prevent Home key from going to beginning of +1
    if (event.key === 'Home') {
      event.preventDefault();
      setTimeout(() => {
        if (input) {
          input.setSelectionRange(3, 3);
        }
      }, 0);
      return;
    }
    
    // Only allow numbers and navigation keys
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Tab', 'Enter', 'Escape'
    ];
    
    // Allow Ctrl+A, Ctrl+C, Ctrl+V, etc.
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    
    if (!allowedKeys.includes(event.key) && !/\d/.test(event.key)) {
      event.preventDefault();
    }
  };

  // Handle click events for phone number inputs
  const handlePhoneClick = (event) => {
    const input = event.target;
    setTimeout(() => {
      const cursorPosition = input.selectionStart || 0;
      if (cursorPosition < 3) {
        input.setSelectionRange(3, 3);
      }
    }, 0);
  };

  // Handle focus events for phone number inputs
  const handlePhoneFocus = (event, fieldName) => {
    const input = event.target;
    setTimeout(() => {
      // Set cursor to after +1 prefix if the field is empty or cursor is in prefix
      if (!formData[fieldName] || input.selectionStart < 3) {
        input.setSelectionRange(3, 3);
      }
    }, 0);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.facilityName?.trim()) {
      newErrors.facilityName = "Facility Name is required";
    }
    
    if (!formData?.phoneNumber?.trim()) {
      newErrors.phoneNumber = "Phone Number is required";
    } else if (!isValidUSPhoneNumber(formData?.phoneNumber)) {
      newErrors.phoneNumber = "Enter a valid US phone number";
    }
    
    if (!formData?.extension?.trim()) {
      newErrors.extension = "Extension is required";
    } else if (!/^\d{3,5}$/.test(formData?.extension)) {
      newErrors.extension = "Extension must be 3-5 digits";
    }
    
    if (!formData?.faxNumber?.trim()) {
      newErrors.faxNumber = "Fax Number is required";
    } else if (!isValidUSPhoneNumber(formData?.faxNumber)) {
      newErrors.faxNumber = "Enter a valid US fax number";
    }
    
    if (!formData?.groupNPI?.trim()) {
      newErrors.groupNPI = "Group NPI is required";
    } else if (!isValidNPI(formData?.groupNPI)) {
      newErrors.groupNPI = "Group NPI must be exactly 10 digits";
    }
    
    if (!formData?.taxId?.trim()) {
      newErrors.taxId = "Tax ID is required";
    } else if (!isValidTaxId(formData?.taxId)) {
      newErrors.taxId = "Tax ID must be exactly 9 digits";
    }
    
    if (!formData?.streetAddress?.trim()) {
      newErrors.streetAddress = "Street Address is required";
    }
    
    if (!formData?.city?.trim()) {
      newErrors.city = "City is required";
    }
    
    if (!formData?.state?.trim()) {
      newErrors.state = "State is required";
    }
    
    if (!formData?.zipCode?.trim()) {
      newErrors.zipCode = "Zip Code is required";
    } else if (!/^\d{5}$/.test(formData?.zipCode)) {
      newErrors.zipCode = "Zip code must be exactly 5 digits";
    }

    if (!formData?.inNetworkIns || formData?.inNetworkIns.length === 0) {
      newErrors.inNetworkIns = "At least one In-Network Insurance Plan is required";
    }

    if (!formData?.networkPlanType || formData?.networkPlanType.length === 0) {
      newErrors.networkPlanType = "At least one In-Network Plan Type is required";
    }

    // Validate placeOfService - can be string, array, or object depending on dropdown type
    if (Array.isArray(formData?.placeOfService)) {
      if (formData?.placeOfService.length === 0) {
        newErrors.placeOfService = "Place of Service is required";
      }
    } else if (typeof formData?.placeOfService === 'string') {
      if (!formData?.placeOfService || formData?.placeOfService.trim() === "") {
        newErrors.placeOfService = "Place of Service is required";
      }
    } else {
      if (!formData?.placeOfService) {
        newErrors.placeOfService = "Place of Service is required";
      }
    }

    // Validate departments (optional but validate format if provided)
    if (formData?.departments && formData?.departments.length > 0) {
      formData?.departments.forEach((dept, index) => {
        if (dept.name && dept.name.length > 100) {
          newErrors[`departments.${index}.name`] = "Department name must not exceed 100 characters";
        }
        
        if (dept.faxNumber && dept.faxNumber?.trim() && !isValidUSPhoneNumber(dept.faxNumber)) {
          newErrors[`departments.${index}.faxNumber`] = "Enter a valid US fax number";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      
      if (facilityId) {
        // Update existing facility - match facility-form.js format exactly
        const updateData = {
          facilityName: formData?.facilityName,
          phoneNumber: cleanPhoneNumber(formData?.phoneNumber),
          extension: formData?.extension,
          faxNumber: cleanPhoneNumber(formData?.faxNumber),
          groupNPI: cleanNPI(formData?.groupNPI),
          groupTaxId: cleanTaxId(formData?.taxId),
          inNetworkInsurancePlans: formData?.inNetworkIns,
          inNetworkInsurancePlanType: formData?.networkPlanType.map(type => 
            typeof type === 'string' ? type : type.value
          ),
          placeOfService: Array.isArray(formData?.placeOfService) 
            ? (formData?.placeOfService[0]?.value || formData?.placeOfService[0] || "")
            : (typeof formData?.placeOfService === 'object' ? formData?.placeOfService?.value : formData?.placeOfService),
          location: {
            streetAddress: formData?.streetAddress,
            city: formData?.city,
            state: formData?.state,
            zipcode: formData?.zipCode
          },
          // Consistent department structure with facility-form.js
          departments: formData?.departments
            .filter(dept => dept.name?.trim() || dept.faxNumber?.trim()) // Only include departments with data
            .map(dept => {
              const department = {
                name: dept.name?.trim() || "",
                faxNumber: dept.faxNumber?.trim() ? cleanPhoneNumber(dept.faxNumber) : ""
              };
              // Include id for existing departments (those that have an id)
              if (dept.id) {
                department.id = dept.id;
              }
              return department;
            })
        };

        console.log("🏥 Submitting update data:", updateData);
        console.log("🏥 Departments being submitted:", updateData.departments);

        const response = await editFacility(facilityId, updateData);
        if (response.statusCode === "SUCCESS") {
          message.success("Facility details updated successfully!");
          onNext();
        } else {
          message.error(response.message || "Failed to update facility details");
        }
      } else {
        // Create new facility - match facility-form.js format exactly
        const payload = {
          facilityName: formData?.facilityName,
          phoneNumber: cleanPhoneNumber(formData?.phoneNumber),
          extension: formData?.extension,
          faxNumber: cleanPhoneNumber(formData?.faxNumber),
          groupNPI: cleanNPI(formData?.groupNPI),
          groupTaxId: cleanTaxId(formData?.taxId),
          inNetworkInsurancePlans: formData?.inNetworkIns,
          inNetworkInsurancePlanType: formData?.networkPlanType.map(type => 
            typeof type === 'string' ? type : type.value
          ),
          placeOfService: Array.isArray(formData?.placeOfService) 
            ? (formData?.placeOfService[0]?.value || formData?.placeOfService[0] || "")
            : (typeof formData?.placeOfService === 'object' ? formData?.placeOfService?.value : formData?.placeOfService),
          location: {
            streetAddress: formData?.streetAddress,
            city: formData?.city,
            state: formData?.state,
            zipcode: formData?.zipCode
          },
          // Consistent department structure with facility-form.js
          departments: formData?.departments
            .filter(dept => dept.name?.trim() || dept.faxNumber?.trim()) // Only include departments with data
            .map(dept => ({
              name: dept.name?.trim() || "",
              faxNumber: dept.faxNumber?.trim() ? cleanPhoneNumber(dept.faxNumber) : ""
            }))
        };

        console.log("🏥 Submitting create data:", payload);
        console.log("🏥 Departments being submitted:", payload.departments);

        const response = await createFacility(payload);
        if (response.statusCode === "SUCCESS") {
          message.success("Facility details saved successfully!");
          onNext();
        } else {
          message.error(response.message || "Failed to save facility details");
        }
      }
    } catch (error) {
      console.error("Error saving facility details:", error);
      message.error("Failed to save facility details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasLoadedData) {
    return (
      <div className="step-loading">
        <div className="loading-spinner"></div>
        <p>Loading facility details...</p>
      </div>
    );
  }

  return (
    <div className={`step-container ${themeMode}`} style={{ padding: "1rem" }}>
      <div className="step-form">
        {/* Facility Details Section */}
        <div className="form-section" style={{ marginBottom: "1rem" }}>
          <h3 className="form-section-title">Facility Details</h3>
          
          <div className="form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "1rem" }}>
            <div className="form-field">
              <label className="form-label">Facility Name*</label>
              <input
                type="text"
                value={formData?.facilityName}
                onChange={(e) => handleInputChange("facilityName", e.target.value)}
                className={`form-input ${errors.facilityName ? "error" : ""}`}
                disabled={isLoading}
              />
              <div className="form-error-container">
                {errors.facilityName && <span className="form-error">{errors.facilityName}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">Phone Number*</label>
              <input
                type="tel"
                value={formData?.phoneNumber ? `+1 ${formData?.phoneNumber}` : "+1 "}
                onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                className={`form-input ${errors.phoneNumber ? "error" : ""}`}
                disabled={isLoading}
                placeholder="+1 (555) 123-4567"
                onKeyDown={(e) => handlePhoneKeyDown(e)}
                onClick={(e) => handlePhoneClick(e)}
                onFocus={(e) => handlePhoneFocus(e, 'phoneNumber')}
              />
              <div className="form-error-container">
                {errors.phoneNumber && <span className="form-error">{errors.phoneNumber}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">Extension*</label>
              <input
                type="text"
                value={formData?.extension}
                onChange={(e) => handleInputChange("extension", e.target.value)}
                className={`form-input ${errors.extension ? "error" : ""}`}
                disabled={isLoading}
                placeholder="123-12345"
              />
              <div className="form-error-container">
                {errors.extension && <span className="form-error">{errors.extension}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">Fax Number*</label>
              <input
                type="tel"
                value={formData?.faxNumber ? `+1 ${formData?.faxNumber}` : "+1 "}
                onChange={(e) => handleInputChange("faxNumber", e.target.value)}
                className={`form-input ${errors.faxNumber ? "error" : ""}`}
                disabled={isLoading}
                placeholder="+1 (555) 123-4567"
                onKeyDown={(e) => handlePhoneKeyDown(e)}
                onClick={(e) => handlePhoneClick(e)}
                onFocus={(e) => handlePhoneFocus(e, 'faxNumber')}
              />
              <div className="form-error-container">
                {errors.faxNumber && <span className="form-error">{errors.faxNumber}</span>}
              </div>
            </div>
          </div>

          <div className="form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "1rem" }}>
            <div className="form-field">
              <label className="form-label">Group NPI*</label>
              <input
                type="text"
                value={formData?.groupNPI}
                onChange={(e) => handleInputChange("groupNPI", e.target.value)}
                className={`form-input ${errors.groupNPI ? "error" : ""}`}
                disabled={isLoading}
                placeholder="1234567890"
              />
              <div className="form-error-container">
                {errors.groupNPI && <span className="form-error">{errors.groupNPI}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">Tax ID*</label>
              <input
                type="text"
                value={formData?.taxId}
                onChange={(e) => handleInputChange("taxId", e.target.value)}
                className={`form-input ${errors.taxId ? "error" : ""}`}
                disabled={isLoading}
                placeholder="123456789"
              />
              <div className="form-error-container">
                {errors.taxId && <span className="form-error">{errors.taxId}</span>}
              </div>
            </div>

            <div className="form-field">
              <InNetworkInsuranceField
                value={formData?.inNetworkIns}
                onChange={(value) => handleInputChange("inNetworkIns", value)}
                themeMode={themeMode}
                hasError={!!errors.inNetworkIns}
                errorMessage={errors.inNetworkIns}
              />
            </div>

            <div className="form-field">
              <label className="form-label">In-Network Plan Types*</label>
              <div style={{ 
                border: errors.networkPlanType ? "2px solid #ff4d4f" : "1px solid #d9d9d9",
                borderRadius: "6px",
                padding: "2px"
              }}>
                <Dropdown
                  value={formData?.networkPlanType}
                  onChange={(value) => handleInputChange("networkPlanType", value)}
                  options={networkPlanTypes}
                  type="multi-select"
                  isSearchable={true}
                  creatable={true}
                  placeholder="Select or add in-network plan types"
                  valueKey="value"
                  labelKey="label"
                  themeMode={themeMode}
                  disabled={isLoading}
                  error={!!errors.networkPlanType}
                  helperText={errors.networkPlanType}
                  style={{ width: "100%", borderRadius: "6px", border: "none" }}
                />
              </div>
            </div>

            <div className="form-field">
              <label className="form-label">Place of Service*</label>
              <div style={{ 
                border: errors.placeOfService ? "2px solid #ff4d4f" : "1px solid #d9d9d9",
                borderRadius: "6px",
                padding: "2px"
              }}>
                <Dropdown
                  value={formData?.placeOfService}
                  onChange={(value) => handleInputChange("placeOfService", value)}
                  options={placeOfServiceOptions}
                  isSearchable={true}
                  creatable={true}
                  placeholder="Select or add place of service"
                  valueKey="value"
                  labelKey="label"
                  themeMode={themeMode}
                  disabled={isLoading}
                  error={!!errors.placeOfService}
                  helperText={errors.placeOfService}
                  style={{ width: "100%", borderRadius: "6px", border: "none" }}
                />
              </div>
            </div>

            {/* Empty spacer fields to maintain 4-column layout */}
            <div className="form-field"></div>
            <div className="form-field"></div>
          </div>

          <div className="form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "1rem" }}>
            <div className="form-field">
              <label className="form-label">Street Address*</label>
              <input
                type="text"
                value={formData?.streetAddress}
                onChange={(e) => handleInputChange("streetAddress", e.target.value)}
                className={`form-input ${errors.streetAddress ? "error" : ""}`}
                disabled={isLoading}
                placeholder="Street Address"
              />
              <div className="form-error-container">
                {errors.streetAddress && <span className="form-error">{errors.streetAddress}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">State*</label>
              <input
                type="text"
                value={formData?.state}
                onChange={(e) => handleInputChange("state", e.target.value)}
                className={`form-input ${errors.state ? "error" : ""}`}
                disabled={isLoading}
                placeholder="State"
              />
              <div className="form-error-container">
                {errors.state && <span className="form-error">{errors.state}</span>}
              </div>
            </div>

            <div className="form-field">
              <label className="form-label">City*</label>
              <input
                type="text"
                value={formData?.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                className={`form-input ${errors.city ? "error" : ""}`}
                disabled={isLoading}
                placeholder="City"
              />
              <div className="form-error-container">
                {errors.city && <span className="form-error">{errors.city}</span>}
              </div>
            </div>

            <div className="form-field">
              <label className="form-label">Zip Code*</label>
              <input
                type="text"
                value={formData?.zipCode}
                onChange={(e) => handleInputChange("zipCode", e.target.value)}
                className={`form-input ${errors.zipCode ? "error" : ""}`}
                disabled={isLoading}
                placeholder="Zip Code"
              />
              <div className="form-error-container">
                {errors.zipCode && <span className="form-error">{errors.zipCode}</span>}
              </div>
            </div>
          </div>
        </div>


        {/* Departments Section */}
        <div className="form-section" style={{ marginBottom: "-1rem" }}>
          <h3 className="form-section-title">
            Departments (Optional) - {formData?.departments.length} department{formData?.departments.length !== 1 ? 's' : ''}
          </h3>
          
          <div style={{ 
            maxHeight: "21vh", 
            overflowY: "auto", 
            border: "1px solid #e0e0e0", 
            borderRadius: "8px", 
            padding: "1rem",
            backgroundColor: themeMode === 'dark' ? '#1a1a1a' : '#fafafa'
          }}>
            {formData?.departments && formData?.departments.length > 0 ? (
              formData?.departments.map((department, index) => {
                // Debug logging for each department render
                console.log(`🏥 Rendering department ${index}:`, department);
                return (
                  <div key={index} className="form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "1rem", marginBottom: "0.5rem", alignItems: "flex-end" }}>
                    <div className="form-field">
                      <label className="form-label">Department Name</label>
                      <input
                        type="text"
                        value={department.name || ""}
                        onChange={(e) => handleDepartmentChange(index, "name", e.target.value)}
                        className={`form-input ${errors[`departments.${index}.name`] ? "error" : ""}`}
                        disabled={isLoading}
                        placeholder="Department Name"
                      />
                      <div className="form-error-container">
                        {errors[`departments.${index}.name`] && <span className="form-error">{errors[`departments.${index}.name`]}</span>}
                      </div>
                    </div>
                    
                    <div className="form-field">
                      <label className="form-label">Department Fax (Optional)</label>
                      <input
                        type="tel"
                        value={department.faxNumber ? `+1 ${department.faxNumber}` : "+1 "}
                        onChange={(e) => handleDepartmentChange(index, "faxNumber", e.target.value)}
                        className={`form-input ${errors[`departments.${index}.faxNumber`] ? "error" : ""}`}
                        disabled={isLoading}
                        placeholder="+1 (555) 123-4567"
                        onKeyDown={(e) => handlePhoneKeyDown(e)}
                        onClick={(e) => handlePhoneClick(e)}
                        onFocus={(e) => handlePhoneFocus(e, 'departmentFax')}
                      />
                      <div className="form-error-container">
                        {errors[`departments.${index}.faxNumber`] && <span className="form-error">{errors[`departments.${index}.faxNumber`]}</span>}
                      </div>
                    </div>

                    {/* Empty field to maintain 4-column layout */}
                    <div className="form-field">
                      {/* Empty space */}
                    </div>
                    
                    {/* Action buttons - moved to 4th column */}
                    <div className="form-field" style={{ 
                      display: "flex", 
                      flexDirection: "row",
                      flexWrap: "nowrap",
                      justifyContent: "flex-end", 
                      alignItems: "center", 
                      gap: "8px", 
                      paddingTop: "1.5rem",
                      minHeight: "2rem"
                    }}>
                      {/* Add Department button - only show on last department */}
                      {index === formData?.departments.length - 1 && (
                        <button
                          type="button"
                          onClick={addDepartment}
                          className="btn-add-department"
                          disabled={isLoading}
                          style={{
                            background: "#e63e2b",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            padding: "8px 12px",
                            fontSize: "12px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            flexShrink: 0
                          }}
                        >
                          Add Department
                        </button>
                      )}
                      
                      {/* Delete/Clear button - right next to Add Department */}
                      {(formData?.departments.length > 1 || department.name || department.faxNumber) && (
                        <button
                          type="button"
                          onClick={() => removeDepartment(index)}
                          className="btn-remove-department"
                          disabled={isLoading}
                          style={{
                            background: "#ff4d4f",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            padding: "8px 12px",
                            fontSize: "12px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            flexShrink: 0
                          }}
                          title={formData?.departments.length > 1 ? "Delete Department" : "Clear Department"}
                        >
                          {formData?.departments.length > 1 ? "Delete" : "Clear"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ 
                padding: "2rem", 
                textAlign: "center", 
                color: themeMode === 'dark' ? '#a6a3bf' : '#666',
                fontStyle: 'italic'
              }}>
                No departments to display. Click "Add Department" to create one.
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="step-actions has-previous">
          <button
            className="btn-previous"
            onClick={onPrevious}
            disabled={isLoading}
          >
            Previous
          </button>
          <button
            className="btn-next"
            onClick={handleNext}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save & Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FacilityDetailsStep; 
